/**
 * PortfolioSelectorBar — extracted from PortfolioDashboard.tsx
 * (Phase c — Portfolio Off-Chain splitting, Pass 2).
 *
 * Renders the row that sits below the dashboard header :
 *  - left side : `<PortfolioSelector>` + 5 widgets (FluxNode, FluxApps,
 *    Cronos staking, Hedera staking, TheGraph). Each widget self-hides
 *    when its data layer has nothing to show.
 *  - right side : compact sync info (API hits, cache hits, token count).
 *
 * No state inside this component — every callback comes from the
 * parent. The only computed value local to the bar is the cache-age
 * label, which is just a passthrough on the `pricesCacheInfo` prop.
 */

import type { Portfolio } from '../../services/portfolioManager';
import type { PricesCacheInfo } from '../../types/portfolio';
import { Icons } from '../Icons';
import { PortfolioSelector } from './PortfolioSelector';
import { FluxNodeStatusWidget } from '../dashboard/FluxNodeStatusWidget';
import { FluxAppsWidget } from '../dashboard/FluxAppsWidget';
import { CronosStakingWidget } from '../dashboard/CronosStakingWidget';
import { HederaStakingWidget } from '../dashboard/HederaStakingWidget';
import { TheGraphWidget } from '../dashboard/TheGraphWidget';

// PricesCacheInfo re-exported for backwards compat with callers that
// imported it from this file before the relocation. Prefer importing
// directly from `src/types/portfolio.ts` going forward.
export type { PricesCacheInfo };

interface PortfolioSelectorBarProps {
  activePortfolio: Portfolio | null;
  onPortfolioChange: (portfolio: Portfolio) => void;
  onCreatePortfolio: () => void;
  onDeletePortfolio: (portfolio: Portfolio) => void;
  onNavigate?: (pageId: string) => void;
  pricesCacheInfo: PricesCacheInfo;
  tokenCount: number;
}

export function PortfolioSelectorBar({
  activePortfolio,
  onPortfolioChange,
  onCreatePortfolio,
  onDeletePortfolio,
  onNavigate,
  pricesCacheInfo,
  tokenCount,
}: PortfolioSelectorBarProps) {
  return (
    <div
      style={{
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <PortfolioSelector
          activePortfolio={activePortfolio}
          onPortfolioChange={onPortfolioChange}
          onCreatePortfolio={onCreatePortfolio}
          onDeletePortfolio={onDeletePortfolio}
        />
        {/* FluxNode Status Widget — only shows if wallets configured */}
        <FluxNodeStatusWidget onNavigate={onNavigate} />
        {/* FluxApps Widget — only shows if apps owner configured */}
        <FluxAppsWidget onNavigate={onNavigate} />
        {/* Cronos Staking Widget — only shows if wallets configured */}
        <CronosStakingWidget onNavigate={onNavigate} />
        {/* Hedera Staking Widget — only shows if accounts configured */}
        <HederaStakingWidget onNavigate={onNavigate} />
        {/* The Graph Widget — only shows if wallets configured */}
        <TheGraphWidget onNavigate={onNavigate} />
      </div>

      {/* Sync info — compact version */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '11px', color: 'var(--muted)' }}>
        {pricesCacheInfo.fromAPI > 0 && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#10b981' }}>
            <Icons.CheckCircle size={11} />
            {pricesCacheInfo.fromAPI} API
          </span>
        )}
        {pricesCacheInfo.fromCache > 0 && pricesCacheInfo.cacheAge && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Icons.Clock size={11} />
            {pricesCacheInfo.fromCache} cache ({pricesCacheInfo.cacheAge})
          </span>
        )}
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Icons.Database size={11} />
          {tokenCount} tokens
        </span>
      </div>
    </div>
  );
}
